"use client"

import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { loadLoomDoc, saveLoomDoc, clearLoomDoc, emptyDoc, setCurrentDocId, type LoomDoc } from "@/lib/storage"
import { addPin, listPins, loomDocFromPin, removePin, type PinRecord } from "@/lib/pins"
import type { Artifact } from "@/lib/artifact"
import ChatPane from "@/components/ChatPane"
import Scrapbook from "@/components/Scrapbook"

export default function Page() {
  // The LoomDoc is the single source of truth for project + transcript +
  // tool-output caches. Every persisted state is a coherent snapshot because
  // the only writer is the effect below, and it always writes the whole doc.
  const [doc, setDoc] = useState<LoomDoc>(() => emptyDoc())
  const [chatKey, setChatKey] = useState("init")
  const docLoaded = useRef(false)

  // Pin state is owned at the page level so the scrapbook (sidebar / drawer)
  // and the inline pin overlays inside the chat both observe the same set
  // without redundant IDB reads.
  const [pins, setPins] = useState<PinRecord[]>([])
  const pinnedIds = useMemo(() => new Set(pins.map((p) => p.id)), [pins])

  // Drawer open state controls the narrow-viewport scrapbook overlay. Has
  // no effect at wide viewports, where the scrapbook is always rendered as
  // a persistent right column.
  const [drawerOpen, setDrawerOpen] = useState(false)

  useEffect(() => {
    loadLoomDoc().then((loaded) => {
      docLoaded.current = true
      setDoc(loaded)
      setChatKey("ready")
    })
    listPins().then(setPins)
  }, [])

  useEffect(() => {
    if (!docLoaded.current) return
    saveLoomDoc(doc)
  }, [doc])

  const refreshPins = useCallback(async () => {
    const fresh = await listPins()
    setPins(fresh)
  }, [])

  const handleTogglePin = useCallback(async (artifact: Artifact, id: string) => {
    if (pinnedIds.has(id)) {
      await removePin(id)
    } else {
      await addPin(artifact)
    }
    await refreshPins()
  }, [pinnedIds, refreshPins])

  const handleOpenPin = useCallback(async (pin: PinRecord) => {
    const loomDoc = loomDocFromPin(pin)
    docLoaded.current = true
    setCurrentDocId(null)
    setDoc(loomDoc)
    setChatKey(`pin-${pin.id}-${Date.now()}`)
    setDrawerOpen(false)
  }, [])

  const handleReset = async () => {
    await clearLoomDoc()
    docLoaded.current = true
    setDoc(emptyDoc())
    setChatKey(`new-${Date.now()}`)
  }

  const handleLoadDoc = (loaded: LoomDoc) => {
    docLoaded.current = true
    setDoc(loaded)
    setChatKey(`load-${Date.now()}`)
  }

  return (
    <main className="h-full">
      <div className="h-full flex">
        {/* Chat surface — fills the remaining width. min-w-0 lets the flex
            child shrink below its intrinsic content width when the sidebar
            takes its 20rem cut at lg+. */}
        <div className="flex-1 min-w-0 relative">
          <ChatPane
            key={chatKey}
            doc={doc}
            onDocChange={setDoc}
            onReset={handleReset}
            onLoadDoc={handleLoadDoc}
            pinnedIds={pinnedIds}
            onTogglePin={handleTogglePin}
          />

          {/* Floating scrapbook trigger — narrow viewports only. Hidden at
              lg+ where the persistent sidebar makes a separate trigger
              unnecessary. Gentle pulsing pin to advertise newly added
              pins. */}
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="lg:hidden fixed top-3 right-3 z-30 flex items-center gap-1.5 rounded-full border border-border bg-background/90 backdrop-blur-sm px-2.5 py-1.5 text-sm shadow-sm hover:bg-muted active:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors cursor-pointer"
            aria-label={`Open scrapbook (${pins.length} pin${pins.length === 1 ? "" : "s"})`}
          >
            <ScrapbookTriggerIcon />
            <span className="font-medium tabular-nums text-foreground min-w-[1ch] text-center">
              {pins.length}
            </span>
          </button>
        </div>

        {/* Persistent sidebar — wide viewports. Same Scrapbook component
            as the drawer below; only the wrapper differs. No close button
            here (sidebar is always visible at this width). */}
        <aside
          className="hidden lg:block lg:w-80 lg:flex-shrink-0 lg:border-l lg:border-border lg:bg-background"
          aria-label="Scrapbook"
        >
          <Scrapbook
            pins={pins}
            pinnedIds={pinnedIds}
            onTogglePin={handleTogglePin}
            onOpenPin={handleOpenPin}
          />
        </aside>

        {/* Drawer overlay — narrow viewports, conditionally rendered. Takes
            the full width of the display so the gallery has room to read.
            No backdrop is needed since the drawer covers the full screen;
            the in-panel close button (top-right of the scrapbook header)
            is the explicit dismiss affordance. */}
        {drawerOpen && (
          <aside
            className="lg:hidden fixed inset-0 z-40 bg-background animate-slide-in-right"
            aria-label="Scrapbook"
          >
            <Scrapbook
              pins={pins}
              pinnedIds={pinnedIds}
              onTogglePin={handleTogglePin}
              onOpenPin={handleOpenPin}
              onClose={() => setDrawerOpen(false)}
            />
          </aside>
        )}
      </div>
    </main>
  )
}

function ScrapbookTriggerIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 32 32"
      aria-hidden="true"
      style={{ transform: "rotate(-22deg)" }}
    >
      <line x1="11" y1="11" x2="26" y2="26" stroke="#777" strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="9.5" cy="9.5" r="6.2" fill="#dc4747" stroke="#9a2a2a" strokeWidth="1.2" />
      <ellipse cx="7" cy="7" rx="2.4" ry="1.5" fill="white" opacity="0.55" transform="rotate(-30 7 7)" />
    </svg>
  )
}
